import { Tooltip } from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import { FcInfo } from 'react-icons/fc';
import { useIntl } from "react-intl";
import { Col, Row } from 'reactstrap';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import { useMobile } from '../Layout/UseResponsive';
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';

export type PartsInsertComponentType = {
    assistanceId: number;
    setQuerySearch: React.Dispatch<React.SetStateAction<string>>;
    querySearch: string;
    setGoToPage: React.Dispatch<React.SetStateAction<number>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    setReloadParts: React.Dispatch<React.SetStateAction<boolean>>;
    reloadParts: boolean;
    isComplete: boolean;
    isAcceptedBudget: boolean;
}

const PartsInsertComponent = ({ assistanceId, setQuerySearch, querySearch, setGoToPage, setCurrentPage, setReloadParts, reloadParts, isComplete, isAcceptedBudget }: PartsInsertComponentType) => {
    const [showClear, setShowClear] = useState<boolean>(false);
    const [isLoadingNewExecution, setIsLoadingNewExecution] = useState<boolean>(false);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [displayValue, setDisplayValue] = useState<string>("");
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isLocked = isAcceptedBudget || isComplete;
    const intl = useIntl();
    const isMobile = useMobile();

    const { availableModules } = useAvailableModulesContext();
    const isLiveSearchActive = availableModules.includes("IsActiveLivePartSearch");

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Sync displayValue with querySearch when not in live search mode
    useEffect(() => {
        if (!isLiveSearchActive) {
            setDisplayValue(querySearch);
        }
    }, [querySearch, isLiveSearchActive]);

    const searchOnClick = () => {
        setGoToPage(1);
        setCurrentPage(1);
        setReloadParts(!reloadParts);
        setShowClear(true);
    }

    const clearOnClick = () => {
        setQuerySearch("");
        setDisplayValue("");
        setGoToPage(1);
        setCurrentPage(1);
        setShowClear(false);
        setReloadParts(!reloadParts);
    }

    const searchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.code === "Enter" && !isLiveSearchActive) {
            searchOnClick()
        }
    }

    const handleLiveSearch = (searchValue: string) => {
        setDisplayValue(searchValue);
        setIsSearching(false);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (searchValue.length >= 2) {
            timeoutRef.current = setTimeout(() => {
                setIsSearching(true);
                // Send search value directly - backend "name" parameter uses Contains
                setQuerySearch(searchValue);
                setGoToPage(1);
                setCurrentPage(1);
                setReloadParts(prev => !prev);
                setShowClear(true);
                setTimeout(() => setIsSearching(false), 100);
            }, 1000);
        } else if (searchValue.length === 0) {
            clearOnClick();
        }
    }

    const handleInputChange = (value: string) => {
        if (isLiveSearchActive) {
            handleLiveSearch(value);
        } else {
            setQuerySearch(value);
            setDisplayValue(value);
            setShowClear(false);
        }
    }

    return (
        <div>
            { }
            <Row style={{ marginBottom: "1rem", alignItems: "center" }}>
                <Col xl={5} lg={5} md={5} sm={12} xs={12}>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <InputComponent xl={10} lg={10} md={10} sm={10} xs={10}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={isLiveSearchActive ? displayValue : querySearch}
                            label={"Procurar Peça"}
                            readOnly={isLocked}
                            showLoadingSkeleton={isLoadingNewExecution || isSearching}
                            onChange={(t: string) => handleInputChange(t)}
                            onEnter={(e) => searchKeyPress(e)}
                            autoSelectOnFocus
                        />
                        {!isLiveSearchActive && (
                            <div style={{ width: "10px", paddingTop: "20px" }}>
                                <Tooltip title={<div> Pesquisar pelo início: valor* <br /> Pesquisar pelo meio: *valor* <br /> Pesquisar pelo fim: *valor </div>} disableFocusListener enterTouchDelay={0} enterNextDelay={100} enterDelay={100} placement={"right"} style={{ cursor: "pointer" }}>
                                    <b>
                                        <FcInfo className='notesIcon' fontSize={16} />
                                    </b>
                                </Tooltip>
                            </div>
                        )}
                    </Row>
                </Col>

                <Col xl={4} lg={4} md={4} sm={12} xs={12}>
                    {isLiveSearchActive ? (
                        showClear && (
                            <ButtonComponent
                                style={{ width: '100%', marginTop: isMobile ? '10px' : '25px' }}
                                onClick={() => clearOnClick()}
                                text={intl.formatMessage({ id: "PartInsertComponent.Clean" })}
                                loading={isLoadingNewExecution}
                                disabled={isLocked}
                            />
                        )
                    ) : (
                        showClear === true ?
                            <ButtonComponent
                                style={{ width: '100%', marginTop: isMobile ? '10px' : '25px' }}
                                onClick={() => {
                                    clearOnClick()
                                }}
                                text={intl.formatMessage({ id: "PartInsertComponent.Clean" })}
                                loading={isLoadingNewExecution}
                                disabled={isLocked}
                            />
                            :
                            <ButtonComponent
                                style={{ width: '100%', marginTop: isMobile ? '10px' : '25px' }}
                                onClick={() => {
                                    searchOnClick()
                                }}
                                text={intl.formatMessage({ id: "PartInsertComponent.Search" })}
                                loading={isLoadingNewExecution}
                                disabled={isLocked}
                            />
                    )}
                </Col>
            </Row>
        </div>
    )
}

export default PartsInsertComponent
